import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { toast } from 'react-hot-toast'
import { motion, AnimatePresence } from 'framer-motion'
import { ChevronDown, ChevronUp, BarChart3, Package, TrendingUp, ArrowUp, X, Clock, Loader2 } from 'lucide-react'
import useStore from '../store/useStore'
import { cachedApiService } from '../services/api'
import Header from '../components/Header'
import FilterPanel from '../components/FilterPanel'
import MainFilters from '../components/MainFilters'
import PeriodSelector from '../components/PeriodSelector'
import ExportButton from '../components/ExportButton'
import KPICards from '../components/KPICards'
import Charts from '../components/Charts'
import DataTableEnhanced from '../components/DataTableEnhanced'
import ErrorMessage from '../components/ErrorMessage'
import ErrorBoundary from '../components/ErrorBoundary'
import EmptyState from '../components/EmptyState'

const FILTER_LABELS = {
  produits: 'Produits',
  importateurs: 'Importateurs',
  fournisseurs: 'Fournisseurs',
  nif_importateur: 'NIF',
  pays_origine: 'Pays',
  incoterm: 'Incoterm',
  code_regime_apc: 'Régime APC',
  annee_declaration: 'Année',
  date_debut: 'Début',
  date_fin: 'Fin',
}

const Dashboard = () => {
  const { filters, hasFilters, updateFilter } = useStore()

  // --- Split state: analytics vs table ---
  const [analyticsData, setAnalyticsData] = useState(null) // KPI + charts + top
  const [tableData, setTableData] = useState(null)         // paginated table only
  const [analyticsLoading, setAnalyticsLoading] = useState(false)
  const [tableLoading, setTableLoading] = useState(false)
  const [analyticsError, setAnalyticsError] = useState(null)
  const [tableError, setTableError] = useState(null)

  const [showCharts, setShowCharts] = useState(true)
  const [rankingBy, setRankingBy] = useState('value')
  const [lastFetched, setLastFetched] = useState(null)
  const [showBackToTop, setShowBackToTop] = useState(false)

  const tableRef = useRef(null)
  const analyticsFetchRef = useRef(0)
  const tableFetchRef = useRef(0)
  const prevFiltersRef = useRef(filters)

  const [tableOptions, setTableOptions] = useState({
    page: 1,
    size: 50,
    sortField: null,
    sortDirection: 'asc'
  })

  // Stable filter signature for comparison
  const filterSignature = useMemo(() => JSON.stringify(filters), [filters])

  // Back-to-top visibility
  useEffect(() => {
    const onScroll = () => setShowBackToTop(window.scrollY > 400)
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  // Reset page to 1 when filters change
  useEffect(() => {
    if (prevFiltersRef.current !== filters) {
      setTableOptions(prev => ({ ...prev, page: 1 }))
      prevFiltersRef.current = filters
    }
  }, [filterSignature])

  // --- Analytics fetch: only depends on filters + rankingBy ---
  const fetchAnalytics = useCallback(async () => {
    if (!hasFilters) {
      setAnalyticsData(null)
      setAnalyticsError(null)
      return
    }

    const fetchId = ++analyticsFetchRef.current
    setAnalyticsLoading(true)
    setAnalyticsError(null)

    try {
      const result = await cachedApiService.queryAnalytics(filters, rankingBy)
      if (fetchId !== analyticsFetchRef.current) return
      if (!result) throw new Error('Aucune donnée reçue du serveur')

      setAnalyticsData(result)
      setLastFetched(new Date())

      if (result.kpi) {
        toast.success(
          `${result.kpi.total_articles?.toLocaleString() || 0} articles trouvés`,
          { duration: 3000 }
        )
      }
    } catch (err) {
      if (fetchId !== analyticsFetchRef.current) return
      console.error('Analytics fetch error:', err)
      setAnalyticsError(err)
      setAnalyticsData(null)
    } finally {
      if (fetchId === analyticsFetchRef.current) setAnalyticsLoading(false)
    }
  }, [filterSignature, hasFilters, rankingBy])

  // --- Table fetch: depends on filters + table options (page/sort) ---
  const fetchTable = useCallback(async () => {
    if (!hasFilters) {
      setTableData(null)
      setTableError(null)
      return
    }

    const fetchId = ++tableFetchRef.current
    setTableLoading(true)
    setTableError(null)

    try {
      const result = await cachedApiService.queryTable(
        filters,
        tableOptions.page,
        tableOptions.size,
        tableOptions.sortField,
        tableOptions.sortDirection
      )
      if (fetchId !== tableFetchRef.current) return
      if (!result) throw new Error('Aucune donnée reçue du serveur')
      setTableData(result.table || result)
    } catch (err) {
      if (fetchId !== tableFetchRef.current) return
      console.error('Table fetch error:', err)
      setTableError(err)
      setTableData(null)
    } finally {
      if (fetchId === tableFetchRef.current) setTableLoading(false)
    }
  }, [filterSignature, hasFilters, tableOptions.page, tableOptions.size, tableOptions.sortField, tableOptions.sortDirection])

  // Analytics effect: debounce 400ms (filters/ranking changes)
  useEffect(() => {
    const timeoutId = setTimeout(fetchAnalytics, 400)
    return () => clearTimeout(timeoutId)
  }, [fetchAnalytics])

  // Table effect: debounce 100ms (near-instant for pagination/sort)
  useEffect(() => {
    const timeoutId = setTimeout(fetchTable, 100)
    return () => clearTimeout(timeoutId)
  }, [fetchTable])

  const handlePageChange = (newPage) => {
    setTableOptions(prev => ({ ...prev, page: newPage }))
    tableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  const handleSortChange = (field, direction) => {
    setTableOptions(prev => ({
      ...prev,
      sortField: field,
      sortDirection: direction,
      page: 1
    })
    )
  }

  const handleRemoveFilter = (key) => {
    if (key === 'date_debut' || key === 'date_fin') {
      updateFilter(key, null)
    } else {
      updateFilter(key, [])
    }
  }

  const activeFilterChips = Object.entries(filters)
    .filter(([, v]) => (Array.isArray(v) ? v.length > 0 : v !== null && v !== undefined && v !== ''))
    .map(([key, value]) => ({
      key,
      label: FILTER_LABELS[key] || key,
      count: Array.isArray(value) ? value.length : 1,
      summary: Array.isArray(value)
        ? value.length <= 2 ? value.join(', ') : `${value.length} sélectionné(s)`
        : String(value),
    }))

  const formatTime = (date) => {
    if (!date) return null
    return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
  }

  // Combined loading for the top loading bar (only shows on analytics fetch, not table pagination)
  const showGlobalLoading = analyticsLoading && !analyticsData

  return (
    <ErrorBoundary>
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
        <Header />

        {/* Main Content */}
        <main className="page-container page-content">
          {/* Filters — always visible, responsive layout */}
          <ErrorBoundary>
            <MainFilters />
          </ErrorBoundary>

          {/* Period selector — always visible */}
          <div style={{ marginTop: 'calc(var(--space-section) * 0.5)' }}>
            <ErrorBoundary>
              <PeriodSelector />
            </ErrorBoundary>
          </div>

          <div style={{ marginTop: 'calc(var(--space-section) * 0.5)' }}>
            <ErrorBoundary>
              <FilterPanel isCollapsible={true} defaultExpanded={false} />
            </ErrorBoundary>
          </div>

          {/* Active Filter Chips */}
          {hasFilters && activeFilterChips.length > 0 && (
            <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-hide" style={{ marginTop: 'calc(var(--space-section) * 0.5)', marginBottom: 'calc(var(--space-section) * 0.25)' }}>
              {activeFilterChips.map(chip => (
                <motion.span
                  key={chip.key}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  className="inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 rounded-full text-xs font-medium bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 border border-primary-200 dark:border-primary-800/50 whitespace-nowrap flex-shrink-0"
                >
                  <span className="font-semibold">{chip.label}:</span>
                  <span className="max-w-[120px] truncate">{chip.summary}</span>
                  <button
                    onClick={() => handleRemoveFilter(chip.key)}
                    className="ml-0.5 p-0.5 rounded-full hover:bg-primary-200 dark:hover:bg-primary-800/50 transition-colors"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </motion.span>
              ))}
            </div>
          )}

          {/* Dashboard Content */}
          <div style={{ marginTop: 'var(--space-section)' }} className="space-y-6">
            {/* Error State */}
            {analyticsError && (
              <ErrorMessage
                error={analyticsError}
                onRetry={fetchAnalytics}
                title="Erreur de chargement des données"
                showRetry={true}
              />
            )}

            {!hasFilters ? (
              <EmptyState />
            ) : (
              <ErrorBoundary>
                {/* KPI Cards + Timestamp */}
                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <KPICards data={analyticsData?.kpi} loading={analyticsLoading} rankingBy={rankingBy} />
                  {lastFetched && !analyticsLoading && (
                    <div className="flex items-center gap-1.5 mt-2 text-[11px] text-gray-400 dark:text-gray-500">
                      <Clock className="w-3 h-3" />
                      <span>Mis à jour à {formatTime(lastFetched)}</span>
                    </div>
                  )}
                </motion.div>

                {/* Section Controls */}
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mt-2">
                  <h2 className="section-title flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-primary-500" />
                    Analyse
                  </h2>

                  <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                    {/* Ranking toggle */}
                    <div className="inline-flex rounded-lg bg-gray-100 dark:bg-gray-800 p-0.5">
                      <button
                        onClick={() => setRankingBy('value')}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-all duration-200 ${
                          rankingBy === 'value'
                            ? 'bg-white dark:bg-gray-700 text-primary-700 dark:text-primary-300 shadow-sm'
                            : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                        }`}
                      >
                        <TrendingUp className="w-3.5 h-3.5" />
                        Valeur
                      </button>
                      <button
                        onClick={() => setRankingBy('quantity')}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-all duration-200 ${
                          rankingBy === 'quantity'
                            ? 'bg-white dark:bg-gray-700 text-primary-700 dark:text-primary-300 shadow-sm'
                            : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                        }`}
                      >
                        <Package className="w-3.5 h-3.5" />
                        Quantité
                      </button>
                    </div>

                    {/* Charts toggle */}
                    <button
                      onClick={() => setShowCharts(!showCharts)}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 ${
                        showCharts
                          ? 'bg-primary-600 text-white hover:bg-primary-700 shadow-sm'
                          : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:border-primary-300 dark:hover:border-primary-600'
                      }`}
                    >
                      <BarChart3 className="w-3.5 h-3.5" />
                      <span className="hidden sm:inline">Graphiques</span>
                      {showCharts ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                    </button>

                    {/* Export button — always visible */}
                    <ExportButton
                      totalRows={analyticsData?.kpi?.total_articles || 0}
                      sortField={tableOptions.sortField}
                      sortDirection={tableOptions.sortDirection}
                    />
                  </div>
                </div>

                {/* Charts */}
                <AnimatePresence>
                  {showCharts && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <Charts data={analyticsData} loading={analyticsLoading} rankingBy={rankingBy} />
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Data Table */}
                <motion.div
                  ref={tableRef}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: 0.1 }}
                  style={{ scrollMarginTop: 'calc(var(--header-height) + 1rem)' }}
                >
                  <DataTableEnhanced
                    data={tableData}
                    loading={tableLoading}
                    onPageChange={handlePageChange}
                    onSortChange={handleSortChange}
                  />
                </motion.div>
              </ErrorBoundary>
            )}
          </div>
        </main>

        {/* Back to top FAB */}
        <AnimatePresence>
          {showBackToTop && (
            <motion.button
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="fixed bottom-6 right-6 z-40 p-3 rounded-full bg-primary-600 text-white shadow-lg shadow-primary-600/25 hover:bg-primary-700 active:scale-95 transition-all"
              aria-label="Retour en haut"
            >
              <ArrowUp className="w-5 h-5" />
            </motion.button>
          )}
        </AnimatePresence>

        {/* Loading bar — only shows on initial analytics load, not table pagination */}
        {showGlobalLoading && (
          <div className="fixed left-0 right-0 z-50 h-0.5" style={{ top: 'var(--header-height)' }}>
            <div className="h-full bg-primary-500 animate-pulse" style={{ width: '100%' }} />
          </div>
        )}
      </div>
    </ErrorBoundary>
  )
}

export default Dashboard
